import { entities, NotificationType, plainToInstance, type types } from "@fraction/shared";
import { chatDedupeKey } from "@fraction/shared-frontend";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { create } from "zustand";

const MAX_NOTIFICATIONS = 50;

type ChatPage = { data: entities.Chat[]; nextCursor: string | null };

export interface StoredNotification {
  notification: types.WsNotification;
  /** The server-side UserNotification ID, if loaded from server */
  serverNotificationId?: string;
  dismissed: boolean;
}

interface NotificationStore {
  notifications: StoredNotification[];

  addNotification: (notification: types.WsNotification, queryClient?: QueryClient) => void;
  loadFromServer: (serverNotifications: entities.UserNotification[]) => void;
  dismissNotification: (id: string) => void;
  dismissMany: (ids: string[]) => void;
  dismissAll: () => void;
  getVisible: () => StoredNotification[];
  getServerNotificationIds: (wsNotificationIds: string[]) => string[];
  getAllServerNotificationIds: () => string[];
}

/**
 * Handle side effects when specific notification types arrive.
 * For CHAT_MESSAGE_RECEIVED, update the React Query chat cache so the chat view stays in sync.
 * Exported so the WS subscription can apply cache updates for the user's OWN
 * echoed chat messages without surfacing them in the notification center.
 */
export function handleNotificationSideEffects(notification: types.WsNotification, queryClient?: QueryClient) {
  if (!queryClient) return;

  if (notification.type === NotificationType.CHAT_MESSAGE_RECEIVED) {
    const chatNotification = notification as types.ChatMessageNotification;
    const { applicationId, chatType, chatData } = chatNotification;

    // Find and update matching chat query cache entries
    // Query keys follow the pattern: ["appChat", applicationId, chatType, isAuthenticated]
    const matchingQueries = queryClient.getQueriesData({
      queryKey: ["appChat", applicationId, chatType],
    });

    for (const [queryKey] of matchingQueries) {
      queryClient.setQueryData<InfiniteData<ChatPage>>(queryKey, (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        const lastPageIndex = oldData.pages.length - 1;
        const lastPage = oldData.pages[lastPageIndex];
        const newChat = plainToInstance(entities.Chat, chatData);
        const existingChats = lastPage.data || [];
        const merged = _.uniqBy(_.sortBy([...existingChats, newChat], "ts"), chatDedupeKey);
        const newPages = [...oldData.pages];
        newPages[lastPageIndex] = { ...lastPage, data: merged };
        return { ...oldData, pages: newPages };
      });
    }
  }
}

export const useNotificationStore = create<NotificationStore>()((set, get) => ({
  notifications: [],

  addNotification: (notification, queryClient) => {
    // Handle side effects (e.g., chat cache update)
    handleNotificationSideEffects(notification, queryClient);

    set((state) => {
      // Agent session updates are handled by the dedicated agent session overlay, not the notification center
      if (notification.type === NotificationType.AGENT_SESSION_UPDATE) {
        return state;
      }

      // Deduplicate by id
      if (state.notifications.some((n) => n.notification.id === notification.id)) {
        return state;
      }

      const updated = [
        {
          notification,
          serverNotificationId: notification.userNotificationId,
          dismissed: false,
        },
        ...state.notifications,
      ].slice(0, MAX_NOTIFICATIONS);

      return { notifications: updated };
    });
  },

  loadFromServer: (serverNotifications) => {
    set((state) => {
      const existingIds = new Set(state.notifications.map((n) => n.notification.id));
      const newEntries: StoredNotification[] = serverNotifications
        .filter((sn) => sn.data && !existingIds.has(sn.data.id))
        .map((sn) => ({
          notification: sn.data!,
          serverNotificationId: sn.id,
          dismissed: false,
        }));

      if (newEntries.length === 0) return state;

      const merged = [...state.notifications, ...newEntries]
        .sort((a, b) => b.notification.timestamp - a.notification.timestamp)
        .slice(0, MAX_NOTIFICATIONS);

      return { notifications: merged };
    });
  },

  dismissNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.notification.id !== id),
    }));
  },

  dismissMany: (ids) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    set((state) => ({
      notifications: state.notifications.filter((n) => !idSet.has(n.notification.id)),
    }));
  },

  dismissAll: () => {
    set({ notifications: [] });
  },

  getVisible: () => {
    return get().notifications.filter((n) => !n.dismissed);
  },

  getServerNotificationIds: (wsNotificationIds) => {
    const idSet = new Set(wsNotificationIds);
    return get()
      .notifications.filter((n) => idSet.has(n.notification.id) && n.serverNotificationId)
      .map((n) => n.serverNotificationId!);
  },

  getAllServerNotificationIds: () => {
    return get()
      .notifications.filter((n) => n.serverNotificationId)
      .map((n) => n.serverNotificationId!);
  },
}));
